import {Component, inject, OnInit} from '@angular/core';
import {NWMControls} from "app/components/Controls/NWMControls.module";
import {APIService} from "app/services/api.service";
import {SharedModule} from "app/modules/shared/shared.module";
import {NgbCalendar, NgbDateStruct, NgbInputDatepicker, NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {PaginationComponent} from "app/modules/document/pagination/pagination.component";
import {AppConfig} from "app/app.config";
import {SideMenuComponent, sideMenuItemType} from "app/components/Controls/side-menu/side-menu.component";
import {AuthenticationService} from "app/services/auth.service";
import {
  AccountantClient,
  DownloadableFile,
  downloadFromUrl,
  sortAndReturn,
  SortColumn,
  SortDirection
} from "app/modules/document/accountant-document/accountant-document.fn";


// This component displays documents for accountant clients.
// These documents were previously rendered in the Document component, but since they are now retrieved from SD,
// this component was introduced specifically for accountant use.
//
// The logic here is largely based on the Document component, with some modifications to preserve the original UI behavior.
// For example, the <side-menu> component is used in the template, so the TypeScript code has been adjusted to provide data in the format expected by that component.
// Or, for the From/To date picker and the reset range behavior, some of the logic may not seem good but it was intentionally kept similar to the previous implementation.
@Component({
  selector: 'app-accountant-document',
  imports: [
    NWMControls,
    SharedModule,
    PaginationComponent,
    SideMenuComponent,
    NgbInputDatepicker
  ],
  templateUrl: './accountant-document.component.html',
  standalone: true,
  styleUrls: ['./accountant-document.component.scss']
})
export class AccountantDocumentComponent implements OnInit {
  private apiService = inject(APIService);
  private config = inject(AppConfig);
  modalService = inject(NgbModal);
  private calendar = inject(NgbCalendar);
  private auth = inject(AuthenticationService);

  clientsById: Record<string, AccountantClient> = {};
  selectedClient?: AccountantClient;
  selectedClientFiles: DownloadableFile[] = [];
  filterText = '';
  currentFilterText = '';
  newDocumentsOnly = false;
  filteredFileList: DownloadableFile[] = [];
  filteredFileListPaged: DownloadableFile[] = [];
  selectedFiles: Set<DownloadableFile> = new Set();
  sortColumn: SortColumn | null = null;
  sortDirection: SortDirection = 'asc';
  lastViewed: string | null = null;

  // Client menu properties
  isCollapse = false;
  clientMenuItems: sideMenuItemType[] = [];
  endDate: Date = new Date();
  startDate: Date = new Date(this.endDate.getFullYear() - 1, 0, 1);
  displayStartDate: string = this.printDate(this.startDate);
  displayEndDate: string = this.printDate(this.endDate);
  minDate: NgbDateStruct = {year: 2000, month: 1, day: 1};
  maxDate: NgbDateStruct = this.toDateStruct(this.endDate);
  startDateModel: NgbDateStruct = this.toDateStruct(this.startDate);
  endDateModel: NgbDateStruct = this.toDateStruct(this.endDate);
  today = this.calendar.getToday();
  disableResetDateButton = true;
  isImpersonated: boolean = false;
  // I didn't use loaderService as the loading wasn't shown instantly
  loading = false;
  loadingClients = false;
  loadingFiles = false;

  constructor() {}

  ngOnInit() {
    this.loading = true;
    this.apiService.get<string>(`${this.config.document.documentsLastViewed}/${this.auth.getCurrentReRg()!.ReRgId!}`).subscribe({next: data => {
      if (data) {
        this.lastViewed = data;
      }
      this.getAccountantClients();
      this.loading = false;
    }, error: () => {
      this.getAccountantClients();
      this.loading = false;
    }});
    this.isImpersonated = !!sessionStorage.getItem('guid');
  }

  getAccountantClients() {
    this.loadingClients = true;
    this.apiService.getAccountantClients().subscribe({
      next: (accountantClients: AccountantClient[]) => {
        this.clientsById = Object.fromEntries(accountantClients.map(c => [c.clientId, c]));

        // Build client menu items for side-menu
        this.clientMenuItems = accountantClients.map(c => ({
          canActivate: false,
          id: c.clientId,
          items: [],
          opened: false,
          title: c.clientName
        }));

        if(accountantClients.length > 0){
          this.changeClient(accountantClients[0].clientId);
        }
        this.loadingClients = false;
      },
      error: () => {this.loadingClients = false;}
    })
  }

  filterFiles() {
    this.currentFilterText = this.filterText;
    const searchPhrase = this.filterText.toLowerCase();
    this.filteredFileList = this.selectedClientFiles.filter(f =>
      f.fileName.toLowerCase().includes(searchPhrase)
    );
    if (this.newDocumentsOnly) {
      this.filteredFileList = this.filteredFileList.filter(f => this.isNewFile(f));
    }
    // Filter by date range using NgbDateStruct
    if (this.startDateModel) {
      const fromDate = new Date(this.startDateModel.year, this.startDateModel.month - 1, this.startDateModel.day);
      fromDate.setHours(0, 0, 0, 0);
      this.filteredFileList = this.filteredFileList.filter(f => {
        const fileDate = new Date(f.updatedAt);
        return fileDate >= fromDate;
      });
    }
    if (this.endDateModel) {
      const toDate = new Date(this.endDateModel.year, this.endDateModel.month - 1, this.endDateModel.day);
      toDate.setHours(23, 59, 59, 999);
      this.filteredFileList = this.filteredFileList.filter(f => {
        const fileDate = new Date(f.updatedAt);
        return fileDate <= toDate;
      });
    }
    this.filteredFileList = sortAndReturn(this.filteredFileList, this.sortColumn, this.sortDirection);
  }

  isNewFile(f: DownloadableFile) {
    return f.downloadedAt === null && this.lastViewed !== null && this.lastViewed < f.updatedAt;
  }

  resetDateRange() {
    this.startDateModel = this.toDateStruct(this.startDate);
    this.endDateModel = this.toDateStruct(this.endDate);
    this.dateChange();
  }

  changeClient(clientId: string) {
    this.selectedFiles.clear();
    this.selectedClient = this.clientsById[clientId];
    this.selectedClientFiles = [];
    this.loadingFiles = true;
    this.apiService.getAccountantClientDocuments(clientId).subscribe({
      next: (documents: DownloadableFile[]) => {
        this.selectedClientFiles = documents || [];
        this.filterFiles();
        this.loadingFiles = false;
      },
      error: () => {this.loadingFiles = false;}
    })
    this.filterFiles();
  }

  dateChange() {
    this.disableResetDateButton = this.checkDisableResetDate();
    this.filterFiles();
  }

  setStartDateToToday(datepicker: NgbInputDatepicker) {
    this.startDateModel = this.today;
    datepicker.close();
    this.dateChange();
  }

  setEndDateToToday(datepicker: NgbInputDatepicker) {
    this.endDateModel = this.today;
    datepicker.close();
    this.dateChange();
  }

  printDate(date: Date) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }

  toDateStruct(date: Date): NgbDateStruct {
    return {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate(),
    };
  }

  // logic gotten from the previous implementation of the document component
  checkDisableResetDate() {
    const startDateStruct = this.toDateStruct(this.startDate);
    const endDateStruct = this.toDateStruct(this.endDate);

    return (
      startDateStruct.day === this.startDateModel.day &&
      startDateStruct.month === this.startDateModel.month &&
      startDateStruct.year === this.startDateModel.year
    ) && (
      endDateStruct.day === this.endDateModel.day &&
      endDateStruct.month === this.endDateModel.month &&
      endDateStruct.year === this.endDateModel.year
    );
  }

  sort(column: SortColumn) {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.filteredFileList = sortAndReturn(this.filteredFileList, this.sortColumn, this.sortDirection);
  }

  downloadFile(file: DownloadableFile, event: Event) {
    event.stopPropagation();
    this.apiService.download(file.downloadToken).subscribe(res => {
      downloadFromUrl(window, res.body!, file.fileName);
      this.setDownloadAtToNowIfNotImpersonated(new Set([file]));
    });
  }

  setDownloadAtToNow(file: DownloadableFile) {
    file.downloadedAt = (new Date()).toISOString();
  }

  viewFile(file: DownloadableFile) {
    const url = this.config.accountantDocuments.downloadUrl + '?downloadToken=' + file.downloadToken + '&preview=true';
    window.open(url, '_blank');
    this.setDownloadAtToNowIfNotImpersonated(new Set([file]));
  }

  toggleFileSelection(file: DownloadableFile) {
    if (this.selectedFiles.has(file)) {
      this.selectedFiles.delete(file);
    } else {
      this.selectedFiles.add(file);
    }
  }

  isFileSelected(file: DownloadableFile): boolean {
    return this.selectedFiles.has(file);
  }

  toggleSelectAll() {
    if (this.areAllPagedFilesSelected()) {
      this.filteredFileListPaged.forEach(file => this.selectedFiles.delete(file));
    } else {
      this.filteredFileListPaged.forEach(file => this.selectedFiles.add(file));
    }
  }

  areAllPagedFilesSelected(): boolean {
    return this.filteredFileListPaged.length > 0 && this.filteredFileListPaged.every(file => this.selectedFiles.has(file));
  }

  downloadSelectedFiles() {
    const tokensToDownload = Array.from(this.selectedFiles).map(f => f.downloadToken);
    if (tokensToDownload.length === 0) return;

    this.apiService.downloadBulk(tokensToDownload).subscribe(res => {
      downloadFromUrl(window, res.body!, `documents-${new Date().toISOString()}.zip`);
      this.setDownloadAtToNowIfNotImpersonated(this.selectedFiles);
      this.selectedFiles.clear();
    });
  }

  setDownloadAtToNowIfNotImpersonated(files: Set<DownloadableFile>) {
    if (!this.isImpersonated) {
      files.forEach(file => this.setDownloadAtToNow(file));
    }
  }

}
